// VMI Scoring Helper
// Computes 0-6 scores for Identify, Protect, Build, Harvest, Manage, and overall readiness percent
// Inputs are normalized and safe with sensible fallbacks so the UI can render without backend changes.

/*
Expected input shape:
{
  readinessTotal: number | null,       // 6..30 (nullable)
  attractivenessTotal: number | null,  // 6..30 (nullable)

  // Optional tuning values (weights can be tweaked later or made admin-configurable)
  weights: {
    identify?: { r: number, a: number },
    protect?:  { r: number, a: number },
    build?:    { r: number, a: number },
    harvest?:  { r: number, a: number },
    manage?:   { r: number, a: number },
  },

  // Optional per-category completion ratios: 0..1, where 1 means all tasks done
  completionRatios: {
    identify?: number,
    protect?: number,
    build?: number,
    harvest?: number,
    manage?: number,
  },

  // Optional per-category triggering event multiplier quality: 'complete' | 'partial' | 'missing'
  teStatus: {
    identify?: 'complete' | 'partial' | 'missing',
    protect?: 'complete' | 'partial' | 'missing',
    build?: 'complete' | 'partial' | 'missing',
    harvest?: 'complete' | 'partial' | 'missing',
    manage?: 'complete' | 'partial' | 'missing',
  },

  // Optional value gap ratio for Build/Harvest: 0..+
  valueGap: {
    currentValue?: number, // > 0
    gapValue?: number,     // >= 0
  },
}
*/

const clamp = (v, min, max) => Math.max(min, Math.min(max, Number(v)));

const normalizeDiag = (total) => {
  // Map 6..30 → 0..1, clamp if out-of-range. Missing becomes 0.4 (soft-penalty default)
  if (total == null) return 0.4;
  return clamp((Number(total) - 6) / 24, 0, 1);
};

const defaultWeights = {
  identify: { r: 0.7, a: 0.3 },
  protect:  { r: 0.8, a: 0.2 },
  build:    { r: 0.3, a: 0.7 },
  harvest:  { r: 0.5, a: 0.5 },
  manage:   { r: 0.6, a: 0.4 },
};

const teMultiplier = (status) => {
  if (status === 'complete') return 1.0;
  if (status === 'partial') return 0.9;
  // treat unknown as soft-penalty missing
  return 0.9;
};

const completionMultiplier = (ratio) => {
  const r = ratio == null ? 0.6 : clamp(ratio, 0, 1);
  // 0 → 0.6, 1 → 1.0, linear in between
  return 0.6 + 0.4 * r;
};

const hasOutstandingTasks = (ratio) => {
  if (ratio == null) return true; // unknown → assume outstanding
  return clamp(ratio, 0, 1) < 1;
};

const valueGapMult = (currentValue, gapValue) => {
  const cv = Number(currentValue || 0);
  const gv = Number(gapValue || 0);
  if (!(cv > 0)) return 1.0;
  const ratio = clamp(gv / cv, 0, 0.4); // cap penalty to 40%
  return 1.0 - ratio;
};

const toSixScale = (x) => clamp(x * 6, 0, 6);

const roundHalf = (n) => Math.round(n * 2) / 2;

export function computeVmiScores(input) {
  const weights = { ...defaultWeights, ...(input?.weights || {}) };
  const r = normalizeDiag(input?.readinessTotal);
  const a = normalizeDiag(input?.attractivenessTotal);

  const comp = input?.completionRatios || {};
  const te = input?.teStatus || {};
  const vg = input?.valueGap || {};

  const categories = ['identify', 'protect', 'build', 'harvest', 'manage'];

  const base = {
    identify: weights.identify.r * r + weights.identify.a * a,
    protect:  weights.protect.r  * r + weights.protect.a  * a,
    build:    weights.build.r    * r + weights.build.a    * a,
    harvest:  weights.harvest.r  * r + weights.harvest.a  * a,
    manage:   weights.manage.r   * r + weights.manage.a   * a,
  };

  const teMult = {
    identify: teMultiplier(te.identify),
    protect:  teMultiplier(te.protect),
    build:    teMultiplier(te.build),
    harvest:  teMultiplier(te.harvest),
    manage:   teMultiplier(te.manage),
  };

  const taskMult = {
    identify: completionMultiplier(comp.identify),
    protect:  completionMultiplier(comp.protect),
    build:    completionMultiplier(comp.build),
    harvest:  completionMultiplier(comp.harvest),
    manage:   completionMultiplier(comp.manage),
  };

  const vgMult = {
    identify: 1.0,
    protect:  1.0,
    build:    valueGapMult(vg.currentValue, vg.gapValue),
    harvest:  valueGapMult(vg.currentValue, vg.gapValue),
    manage:   1.0,
  };

  const caps = {
    identify: hasOutstandingTasks(comp.identify) ? 5 : 6,
    protect:  hasOutstandingTasks(comp.protect)  ? 5 : 6,
    build:    hasOutstandingTasks(comp.build)    ? 5 : 6,
    harvest:  hasOutstandingTasks(comp.harvest)  ? 5 : 6,
    manage:   hasOutstandingTasks(comp.manage)   ? 5 : 6,
  };

  const rawScores = {};
  categories.forEach((c) => {
    const raw = toSixScale(base[c] * teMult[c] * taskMult[c] * vgMult[c]);
    rawScores[c] = roundHalf(Math.min(caps[c], raw));
  });

  const labeled = [
    { id: 'identify', label: 'Identify', score: rawScores.identify, fullMark: 6 },
    { id: 'protect',  label: 'Protect',  score: rawScores.protect,  fullMark: 6 },
    { id: 'build',    label: 'Build',    score: rawScores.build,    fullMark: 6 },
    { id: 'harvest',  label: 'Harvest',  score: rawScores.harvest,  fullMark: 6 },
    { id: 'manage',   label: 'Manage',   score: rawScores.manage,   fullMark: 6 },
  ];

  const total = labeled.reduce((sum, d) => sum + d.score, 0);
  const overallPct = Math.round((total / (6 * labeled.length)) * 100);

  return {
    categories: labeled, // for RadialChart
    overallPercent: overallPct,
  };
}

export default computeVmiScores;


